import React, { useState, useEffect } from 'react';
import { Contact, CreateContactData } from '../types';
import { contactsApi } from '../api';
import { useAuth } from '../AuthContext';
import { theme } from '../theme';

const ContactInfo: React.FC = () => {
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingContact, setEditingContact] = useState<Contact | null>(null);
    const { isAdmin } = useAuth();

    useEffect(() => {
        loadContacts();
    }, []);

    const loadContacts = async () => {
        try {
            setIsLoading(true);
            const data = await contactsApi.getContacts();
            setContacts(data);
        } catch (err) {
            setError('Kunne ikke laste kontaktinformasjon');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Er du sikker på at du vil slette denne kontakten?')) {
            return;
        }
        try {
            await contactsApi.deleteContact(id);
            setContacts(contacts.filter(c => c.id !== id));
        } catch (err) {
            setError('Kunne ikke slette kontakten');
        }
    };

    const handleContactCreated = (newContact: Contact) => {
        setContacts([...contacts, newContact]);
        setShowCreateForm(false);
    };

    const handleContactUpdated = (updatedContact: Contact) => {
        setContacts(contacts.map(c => c.id === updatedContact.id ? updatedContact : c));
        setEditingContact(null);
    };

    if (isLoading) {
        return <div style={styles.loading}>Laster...</div>;
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Kontaktinformasjon</h1>
                {isAdmin && (
                    <button
                        style={styles.createButton}
                        onClick={() => setShowCreateForm(true)}
                    >
                        + Ny kontakt
                    </button>
                )}
            </div>

            {error && <div style={styles.error}>{error}</div>}

            {showCreateForm && (
                <ContactForm
                    onClose={() => setShowCreateForm(false)}
                    onSaved={handleContactCreated}
                />
            )}

            {editingContact && (
                <ContactForm
                    contact={editingContact}
                    onClose={() => setEditingContact(null)}
                    onSaved={handleContactUpdated}
                />
            )}

            {contacts.length === 0 ? (
                <div style={styles.empty}>Ingen kontakter registrert ennå</div>
            ) : (
                <div style={styles.contactGrid}>
                    {contacts.map(contact => (
                        <div key={contact.id} style={styles.contactCard}>
                            {isAdmin && (
                                <div style={styles.cardActions}>
                                    <button
                                        style={styles.editButton}
                                        onClick={() => setEditingContact(contact)}
                                        title="Rediger kontakt"
                                    >
                                        ✏️
                                    </button>
                                    <button
                                        style={styles.deleteButton}
                                        onClick={() => handleDelete(contact.id)}
                                        title="Slett kontakt"
                                    >
                                        ✕
                                    </button>
                                </div>
                            )}
                            <div style={styles.contactRole}>{contact.role}</div>
                            <h3 style={styles.contactName}>{contact.contactPerson}</h3>
                            {contact.company && (
                                <div style={styles.contactCompany}>{contact.company}</div>
                            )}
                            <div style={styles.contactDetails}>
                                {contact.phone && (
                                    <div style={styles.contactDetail}>
                                        <span style={styles.detailIcon}>📞</span>
                                        <a href={`tel:${contact.phone}`} style={styles.contactLink}>
                                            {contact.phone}
                                        </a>
                                    </div>
                                )}
                                {contact.email && (
                                    <div style={styles.contactDetail}>
                                        <span style={styles.detailIcon}>✉️</span>
                                        <a href={`mailto:${contact.email}`} style={styles.contactLink}>
                                            {contact.email}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

interface ContactFormProps {
    contact?: Contact;
    onClose: () => void;
    onSaved: (contact: Contact) => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ contact, onClose, onSaved }) => {
    const [contactPerson, setContactPerson] = useState(contact?.contactPerson || '');
    const [company, setCompany] = useState(contact?.company || '');
    const [role, setRole] = useState(contact?.role || '');
    const [phone, setPhone] = useState(contact?.phone || '');
    const [email, setEmail] = useState(contact?.email || '');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const isEditing = !!contact;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const data: CreateContactData = {
                contactPerson,
                company,
                role,
                phone: phone || undefined,
                email: email || undefined
            };
            
            let savedContact: Contact;
            if (isEditing) {
                savedContact = await contactsApi.updateContact(contact.id, data);
            } else {
                savedContact = await contactsApi.createContact(data);
            }
            onSaved(savedContact);
        } catch (err) {
            setError(isEditing ? 'Kunne ikke oppdatere kontakten' : 'Kunne ikke opprette kontakten');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={styles.modal}>
            <div style={styles.modalContent}>
                <div style={styles.modalHeader}>
                    <h2>{isEditing ? 'Rediger kontakt' : 'Ny kontaktperson'}</h2>
                    <button style={styles.closeButton} onClick={onClose}>✕</button>
                </div>

                {error && <div style={styles.error}>{error}</div>}

                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Kontaktperson *</label>
                        <input
                            type="text"
                            value={contactPerson}
                            onChange={(e) => setContactPerson(e.target.value)}
                            style={styles.input}
                            placeholder="Ola Nordmann"
                            required
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Firma</label>
                        <input
                            type="text"
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                            style={styles.input}
                            placeholder="Firmanavn AS"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Rolle *</label>
                        <input
                            type="text"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            style={styles.input}
                            placeholder="f.eks. Utbygger, Vaktmester"
                            required
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Telefon</label>
                        <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            style={styles.input}
                            placeholder="+47 123 45 678"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>E-post</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            style={styles.input}
                            placeholder="kontakt@firma.no"
                        />
                    </div>

                    <div style={styles.formActions}>
                        <button type="button" style={styles.cancelButton} onClick={onClose}>
                            Avbryt
                        </button>
                        <button type="submit" style={styles.submitButton} disabled={isLoading}>
                            {isLoading ? 'Lagrer...' : (isEditing ? 'Lagre endringer' : 'Legg til kontakt')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        maxWidth: '900px',
        margin: '0 auto',
        padding: '16px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
    },
    title: {
        margin: 0,
        color: '#333',
    },
    createButton: {
        padding: '12px 24px',
        backgroundColor: theme.primaryColor,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
    },
    loading: {
        textAlign: 'center',
        padding: '48px',
        color: '#666',
    },
    error: {
        padding: '12px',
        backgroundColor: '#ffebee',
        color: '#c62828',
        borderRadius: '4px',
        marginBottom: '16px',
    },
    empty: {
        textAlign: 'center',
        padding: '48px 16px',
        color: '#666',
        backgroundColor: 'rgba(255,255,255,0.9)',
        borderRadius: '8px',
    },
    contactGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: '16px',
    },
    contactCard: {
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #eee',
        position: 'relative',
    },
    cardActions: {
        position: 'absolute',
        top: '12px',
        right: '12px',
        display: 'flex',
        gap: '4px',
    },
    editButton: {
        background: 'none',
        border: 'none',
        fontSize: '14px',
        cursor: 'pointer',
        color: '#999',
        padding: '4px',
    },
    deleteButton: {
        background: 'none',
        border: 'none',
        fontSize: '16px',
        cursor: 'pointer',
        color: '#999',
        padding: '4px',
    },
    contactRole: {
        display: 'inline-block',
        backgroundColor: '#e8f5e9',
        color: theme.primaryColor,
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold',
        marginBottom: '12px',
    },
    contactName: {
        margin: '0 0 4px 0',
        fontSize: '18px',
        color: '#333',
    },
    contactCompany: {
        color: '#666',
        fontSize: '14px',
        marginBottom: '12px',
    },
    contactDetails: {
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid #eee',
    },
    contactDetail: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
        fontSize: '14px',
    },
    detailIcon: {
        fontSize: '14px',
    },
    contactLink: {
        color: theme.primaryColor,
        textDecoration: 'none',
    },
    modal: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
    },
    modalContent: {
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        width: '100%',
        maxWidth: '450px',
        maxHeight: '85vh',
        overflow: 'auto',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '20px',
        cursor: 'pointer',
        color: '#666',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    label: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#333',
    },
    input: {
        padding: '10px 12px',
        fontSize: '16px',
        border: '1px solid #ddd',
        borderRadius: '4px',
    },
    formActions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginTop: '8px',
    },
    cancelButton: {
        padding: '10px 20px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #ddd',
        borderRadius: '4px',
        cursor: 'pointer',
    },
    submitButton: {
        padding: '10px 20px',
        backgroundColor: theme.primaryColor,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 'bold',
    },
};

export default ContactInfo;
